import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiClient from "../../methods/api/apiClient";
import loader from "../../methods/loader";
import { Link } from "react-router-dom";
import "./style.scss";
import AuthLayout from "../../components/AuthLayout";
import { useDispatch } from "react-redux";
import { login_success } from "../../actions/user";
import { FiEye, FiEyeOff } from "react-icons/fi";
import WebsiteLogo from "../../components/global/WebsiteSettings/WebsiteLogo";
import { MdEmail, MdPassword } from "react-icons/md";
import { HiEye, HiEyeOff } from "react-icons/hi";
import { IoChevronBackOutline } from "react-icons/io5";

const OTP_LENGTH = 6;
const RESEND_SECONDS = 60;

const Login = () => {
  const history = useNavigate();
  const dispatch = useDispatch();
  const [username, setUsername] = useState("");
  const [remember, setRemember] = useState(false);
  const [password, setPassword] = useState("");
  const [eyes, setEyes] = useState({
    password: false,
    confirmPassword: false,
    currentPassword: false,
  });

  // OTP state
  const [showOtp, setShowOtp] = useState(false);
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(""));
  const [otpError, setOtpError] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [otpLoading, setOtpLoading] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // stored login response data for post-OTP dispatch
  const pendingUserData = useRef<any>(null);
  const pendingToken = useRef<string>("");

  useEffect(() => {
    if (localStorage.getItem("token")) {
      history("/dashboard");
    }
    let r = localStorage.getItem("remember");
    if (r) {
      let data = JSON.parse(r);
      setUsername(data.email);
      setPassword(data.password);
      setRemember(true);
    }
  }, []);

  // Countdown timer for resend
  useEffect(() => {
    if (resendTimer <= 0) {
      if (timerRef.current) clearInterval(timerRef.current);
      return;
    }
    timerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [resendTimer]);

  const startResendTimer = () => setResendTimer(RESEND_SECONDS);

  // ─── Original submit — unchanged logic, only added OTP screen trigger ───
  const hendleSubmit = (e: any) => {
    e.preventDefault();
    let data: any = {
      email: username,
      password,
    };
    loader(true);
    ApiClient.post(`user/admin/login`, data).then(async (res) => {
      if (res.success) {
        if (remember) {
          localStorage.setItem("remember", JSON.stringify(data));
        } else {
          localStorage.removeItem("remember");
        }
        // Store token & user data to be used after OTP verification
        pendingToken.current = res?.data?.access_token;
        pendingUserData.current = res?.data;

        // Show OTP screen instead of navigating directly
        setShowOtp(true);
        setOtp(Array(OTP_LENGTH).fill(""));
        setOtpError("");
        startResendTimer();
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      }
      loader(false);
    });
  };

  // ─── OTP input handlers ───
  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;
    const updated = [...otp];
    updated[index] = value;
    setOtp(updated);
    setOtpError("");
    if (value && index < OTP_LENGTH - 1) {
      otpRefs.current[index + 1]?.focus();
    }
  };

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpRefs.current[index - 1]?.focus();
    }
    if (e.key === "ArrowLeft" && index > 0) otpRefs.current[index - 1]?.focus();
    if (e.key === "ArrowRight" && index < OTP_LENGTH - 1)
      otpRefs.current[index + 1]?.focus();
  };

  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, OTP_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const updated = Array(OTP_LENGTH).fill("");
    pasted.split("").forEach((ch, i) => (updated[i] = ch));
    setOtp(updated);
    const nextEmpty = Math.min(pasted.length, OTP_LENGTH - 1);
    otpRefs.current[nextEmpty]?.focus();
  };

  // ─── OTP verify submit ───
  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join("");
    if (code.length < OTP_LENGTH) {
      setOtpError("Please enter the complete 6-digit code.");
      return;
    }
    setOtpLoading(true);
    loader(true);
    ApiClient.post(`user/verifyOtp`, {
      email: username,
      otp: code,
    },true).then((res: any) => {
      if (res.success) {
        // Complete the login — same as original flow
        localStorage.setItem("token", res?.data?.access_token);
        dispatch(login_success(res?.data));
        history(`/dashboard`);
      } else {
        setOtpError(res?.message || "Invalid OTP. Please try again.");
        setOtp(Array(OTP_LENGTH).fill(""));
        setTimeout(() => otpRefs.current[0]?.focus(), 50);
      }
      setOtpLoading(false);
      loader(false);
    });
  };

  // ─── Resend OTP ───
  const handleResend = () => {
    if (resendTimer > 0) return;
    loader(true);
    ApiClient.put(`user/resend/otp`, { email: username }).then((res: any) => {
      if (res.success) {
        setOtp(Array(OTP_LENGTH).fill(""));
        setOtpError("");
        startResendTimer();
        setTimeout(() => otpRefs.current[0]?.focus(), 100);
      } else {
        setOtpError(res?.message || "Failed to resend OTP. Please try again.");
      }
      loader(false);
    });
  };

  // ─── OTP Screen ───
  if (showOtp) {
    return (
      <AuthLayout>
          <div className="auth-page grid lg:grid-cols-2 grid-cols-1 gap-3 max-w-[1100px] w-full rounded-[20px] bg-[#FFFFFF1A] sm:p-7 p-5 mx-auto">
            <div className="w-[400px] lg:block hidden ">
              <img
                src="/img/auth-main.webp"
                className="rounded-[20px] max-h-[520px] "
              />
            </div>
            <div className="w-[360px] max-w-full m-auto">
              <form className="" onSubmit={handleOtpSubmit}>
                {/* Logo */}
                <div className="relative mb-4 border-b border-gray-100 pb-4">
                  <img
                    src="/img/logo.png"
                    className=" mx-auto max-w-[170px] object-contain"
                    alt="login-image"
                  />
                </div>

                {/* Heading */}
                <div className="mb-6">
                  <h3 className="xl:text-[24px] lg:text-[22px] sm:text-[20px] text-[18px] font-semibold text-[#fff] text-center">
                    Verify OTP
                  </h3>
                  <p className="text-[12px] sm:text-[14px] font-normal text-[#BCBCBC] text-center mt-2">
                    A 6-digit code was sent to{" "}
                    <span className="font-semibold text-[#FFF]">
                      {username}
                    </span>
                  </p>
                </div>

                {/* OTP Boxes */}
                <div className="flex gap-2 justify-between mb-2">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      ref={(el) => (otpRefs.current[i] = el)}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(i, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(i, e)}
                      onPaste={i === 0 ? handleOtpPaste : undefined}
                      className={`w-full aspect-square text-center text-xl text-[#fff] text-sm bg-[#FFFFFF2B] rounded-[10px] font-bold outline-none transition-all
                      ${otpError ? "bg-[#FFFFFF2B]" : "bg-[#FFFFFF2B]"}
                    `}
                      style={{ maxWidth: "56px" }}
                    />
                  ))}
                </div>

                {/* Error */}
                {otpError && (
                  <p className="text-red-500 text-xs mt-1 mb-2">{otpError}</p>
                )}

                {/* Resend */}
                <div className="flex justify-end mt-3 mb-6">
                  {resendTimer > 0 ? (
                    <span className="text-sm text-[#BCBCBC]">
                      Resend in{" "}
                      <span className="font-semibold text-[#CC9900]">
                        {resendTimer}s
                      </span>
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={handleResend}
                      className="text-sm text-[#CC9900] font-semibold hover:underline"
                    >
                      Resend OTP
                    </button>
                  )}
                </div>

                {/* Verify button */}
                <button
                  type="submit"
                  disabled={otpLoading}
                  className="w-full py-2.5 px-4 text-sm font-semibold tracking-wider rounded-full text-white !bg-[#CC9900] hover:opacity-80 focus:outline-none"
                >
                  {otpLoading ? "Verifying..." : "Verify & Sign in"}
                </button>

                {/* Back */}
                <div className="mt-4 text-center">
                  <button
                    type="button"
                    onClick={() => {
                      setShowOtp(false);
                      setOtp(Array(OTP_LENGTH).fill(""));
                      setOtpError("");
                    }}
                    className="flex items-center gap-1  justify-center text-sm text-[#FFFF] hover:underline mx-auto"
                  >
                    <IoChevronBackOutline /> Back to Sign in
                  </button>
                </div>
              </form>
            </div>
          </div>
      </AuthLayout>
    );
  }

  // ─── Original Login Screen — completely unchanged ───
  return (
    <>
      <AuthLayout>
          <div className="auth-page grid lg:grid-cols-2 grid-cols-1 gap-3 max-w-[1100px] w-full rounded-[20px] bg-[#FFFFFF1A] sm:p-7 p-5 mx-auto">
            <div className="w-[400px] lg:block hidden ">
              <img
                src="/img/auth-main.webp"
                className="rounded-[20px] max-h-[520px] "
              />
            </div>
            <div className="w-[360px] max-w-full m-auto">
              <form className="" onSubmit={hendleSubmit}>
                <div className="relative  mb-4 ">
                  <img
                    src="/img/logo.png"
                    className=" mx-auto max-w-[170px] object-contain"
                    alt="login-image"
                  />
                </div>

                <div className="mb-8">
                  <h3 className="xl:text-[24px] lg:text-[22px] sm:text-[20px] text-[18px] font-semibold text-[#fff] text-center ">
                    Sign in
                  </h3>
                </div>

                <div className="relative flex items-center">
                  <input
                    name="email"
                    type="email"
                    value={username}
pattern="^[^\s@]+@[^\s@]+\.[^\s@]{2,}$"
    onInvalid={(e:any) => e.target.setCustomValidity("Please enter a valid email (example: ex@example.com)")}
    onInput={(e:any) => e.target.setCustomValidity("")}
                    onChange={(e) => setUsername(e.target.value?.trim())}
                    required
                    className="w-full text-[#fff] text-sm bg-[#FFFFFF2B] rounded-[10px] outline-none px-3 pr-10 py-3"
                    placeholder="Enter email"
                  />
                </div>

                <div className="mt-6">
                  <div className="relative flex items-center">
                    <input
                      type={eyes.password ? "text" : "password"}
                      className="w-full text-[#fff] text-sm bg-[#FFFFFF2B] rounded-[10px] outline-none px-3 pr-10 py-3"
                      value={password}
                      onChange={(e) => setPassword(e.target.value?.trim())}
                      placeholder="Password"
                      required
                    />
                    {eyes.password ? (
                      <HiEye
                        className="top-[14px] right-[15px] absolute text-[#bbb] cursor-pointer"
                        onClick={() =>
                          setEyes({ ...eyes, password: !eyes.password })
                        }
                      />
                    ) : (
                      <HiEyeOff
                        className="top-[14px] right-[15px] absolute text-[#bbb] cursor-pointer"
                        onClick={() =>
                          setEyes({ ...eyes, password: !eyes.password })
                        }
                      />
                    )}
                  </div>
                </div>

                <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
                  <div className="flex items-center">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={remember}
                        onChange={(e) => setRemember(e.target.checked)}
                        className="mr-2 h-4 w-4 cursor-pointer"
                        style={{ accentColor: "#CC9900" }}
                      />{" "}
                      <span className="text-[14px] font-normal text-[#FFF]">
                        Remember Me
                      </span>
                    </label>
                  </div>

                  <div>
                    <Link
                      to="/forgotpassword"
                      className="text-[#CC9900] text-sm font-semibold hover:underline"
                    >
                      Forgot Password?
                    </Link>
                  </div>
                </div>

                <div className="mt-8">
                  <button
                    type="submit"
                    className="w-full py-2.5 px-4 text-sm font-semibold tracking-wider rounded-full text-white !bg-[#CC9900] hover:opacity-80 focus:outline-none"
                  >
                    Sign in
                  </button>
                </div>
              </form>
            </div>
          </div>
      </AuthLayout>
    </>
  );
};

export default Login;
